/* Shopify's native customer form owns signup, consent, validation and captcha. */
(function (scope) {
  'use strict';
  const KEYS = {
    subscribed: 'emery:welcome:subscribed:v1',
    dismissed: 'emery:welcome:dismissed:v1',
    shown: 'emery:welcome:shown:v1',
    pending: 'emery:welcome:pending:v1',
    collapsed: 'emery:welcome:collapsed:v1'
  };
  const DAY = 86400000;
  const pageMemory = new Map();

  function isVisibleChallenge(frame, dialog, view) {
    if (frame.tagName?.toLowerCase() !== 'iframe' || dialog.contains(frame)) return false;
    const title = frame.getAttribute('title') || '';
    if (!/hcaptcha.*challenge|challenge.*hcaptcha/i.test(title)) return false;
    const rect = frame.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0 || rect.bottom <= 0 || rect.right <= 0 || rect.top >= view.innerHeight || rect.left >= view.innerWidth) return false;
    for (let element = frame; element; element = element.parentElement) {
      const style = view.getComputedStyle(element);
      if (element.hidden || element.getAttribute('aria-hidden') === 'true' || style.display === 'none'
        || style.visibility === 'hidden' || style.visibility === 'collapse' || Number(style.opacity) === 0) return false;
    }
    return true;
  }

  function createCaptchaHandoff(env) {
    let armed = false;
    let suspended = false;
    let currentBlocker = null;

    function check() {
      if (!env.isActive()) { armed = false; suspended = false; currentBlocker = null; return; }
      const blocker = (armed ? env.findChallenge() : null) || env.findBlockingModal?.();
      if (blocker) {
        if (!suspended && env.suspend(blocker) !== false) {
          suspended = true;
          currentBlocker = blocker;
        } else if (suspended && blocker !== currentBlocker) {
          currentBlocker = blocker;
          env.focusBlocker?.(blocker);
        }
      } else if (suspended && env.canResume()) {
        if (env.resume() !== false) { suspended = false; currentBlocker = null; }
      }
    }

    // Arming records no signup result and never submits the form. Shopify owns
    // the original POST, any challenge, and its continuation after verification.
    return { arm() { armed = true; check(); }, check, stop() { armed = false; suspended = false; currentBlocker = null; }, get suspended() { return suspended; } };
  }

  function triggerDelay(value) {
    const delay = Number(value);
    return value !== '' && value != null && Number.isFinite(delay) && delay >= 0 ? Math.min(30, delay) * 1000 : 5000;
  }

  function scheduleTrigger(env) {
    return env.setTimeout(env.trigger, triggerDelay(env.delay));
  }

  function createController(env) {
    const config = env.config || {};
    const memory = env.memory || new Map();
    const now = () => env.now?.() ?? Date.now();
    let shown = false;
    let dismissed = false;
    let ready = false;
    let response = null;
    let forcedPreview = false;
    let collapsed = false;
    let reopening = false;
    let disposed = false;

    function read(kind, key) {
      const memoryKey = `${kind}:${key}`;
      if (memory.has(memoryKey)) return memory.get(memoryKey);
      try { return env.storage?.(kind)?.getItem(key) ?? memory.get(memoryKey) ?? null; }
      catch { return memory.get(memoryKey) ?? null; }
    }

    function write(kind, key, value) {
      const memoryKey = `${kind}:${key}`;
      memory.set(memoryKey, value === null ? null : String(value));
      try {
        if (value === null) env.storage?.(kind)?.removeItem(key);
        else env.storage?.(kind)?.setItem(key, String(value));
      } catch { /* Optional browser storage must not break the native form. */ }
    }

    function excluded() {
      return /^(cart|password|captcha)$/.test(config.pageType || '')
        || (config.pageType || '').startsWith('customers/')
        || /(?:^|\/)(?:cart|checkout|checkouts|account|password|challenge|captcha)(?:\/|$)/.test(env.pathname?.() || '');
    }

    function recentPopupSubmission() {
      try {
        const pending = JSON.parse(read('session', KEYS.pending) || 'null');
        return pending?.id === config.formId && Number(pending.at) <= now() && now() - Number(pending.at) < 30 * 60000;
      } catch { return false; }
    }

    function verifiedResponse() {
      if (!['success', 'error'].includes(config.formState)) return null;
      const returnHash = env.hash?.() || '';
      const ownReturn = returnHash ? returnHash === `#${config.formId}` : recentPopupSubmission();
      return ownReturn ? config.formState : null;
    }

    function inCooldown(key) {
      const timestamp = Number(read('local', key));
      return timestamp > 0 && now() - timestamp < Math.max(1, Number(config.repeatDays) || 5) * DAY;
    }

    function ineligible() {
      return config.knownCustomer || read('local', KEYS.subscribed) === 'true' || inCooldown(KEYS.dismissed);
    }

    function suppressed() {
      return ineligible() || read('session', KEYS.shown) === 'true';
    }

    function syncTeaser() {
      const visible = !disposed && collapsed && !shown && !dismissed && !excluded()
        && (forcedPreview || !ineligible()) && !env.isHidden?.() && !env.isBusy?.();
      env.setTeaser?.(visible, shown);
      return visible;
    }

    function attempt() {
      syncTeaser();
      if (disposed || !ready || shown || dismissed || excluded() || (collapsed && !reopening)) return false;
      if (!response && !forcedPreview && (reopening ? ineligible() : suppressed())) return false;
      if (env.isHidden?.() || env.isBusy?.()) return false;
      if (env.open?.(response || 'signup') === false) return false;
      shown = true;
      collapsed = false;
      reopening = false;
      if (!forcedPreview) write('session', KEYS.shown, 'true');
      if (response) {
        if (response === 'success') {
          write('local', KEYS.subscribed, 'true');
          write('local', KEYS.collapsed, null);
        }
        write('session', KEYS.pending, null);
        env.consumeReturn?.();
      }
      syncTeaser();
      return true;
    }

    function initialize() {
      response = verifiedResponse();
      forcedPreview = config.preview === true || config.previewDelayed === true;
      if (response === 'success') {
        write('local', KEYS.subscribed, 'true');
        write('local', KEYS.collapsed, null);
      }
      if (!response && !forcedPreview && !ineligible() && !excluded()) collapsed = inCooldown(KEYS.collapsed);
      if (response || config.preview) {
        ready = true;
        attempt();
      }
      syncTeaser();
      return { needsTrigger: !response && !config.preview && !collapsed && !excluded() && (forcedPreview || !suppressed()) };
    }

    function trigger() { ready = true; return attempt(); }
    function preview() { forcedPreview = true; dismissed = false; collapsed = false; ready = true; return attempt(); }
    function collapse() {
      if (!shown || disposed) return;
      if (response === 'success' || (!forcedPreview && (config.knownCustomer || read('local', KEYS.subscribed) === 'true'))) {
        dismiss();
        return;
      }
      shown = false;
      collapsed = true;
      ready = false;
      reopening = false;
      if (!forcedPreview) write('local', KEYS.collapsed, now());
      env.close?.();
      if (syncTeaser()) env.focusTeaser?.();
    }
    function reopen() {
      if (disposed || !collapsed || dismissed || excluded() || (!forcedPreview && ineligible())) return false;
      reopening = true;
      ready = true;
      return attempt();
    }
    function dismiss() {
      if (!shown && !collapsed) return;
      const wasShown = shown;
      dismissed = true;
      shown = false;
      collapsed = false;
      reopening = false;
      ready = false;
      if (!forcedPreview) {
        write('local', KEYS.collapsed, null);
        write('local', KEYS.dismissed, now());
        write('session', KEYS.shown, 'true');
      }
      if (wasShown) env.close?.();
      syncTeaser();
    }
    function stop() {
      disposed = true;
      const wasShown = shown;
      shown = false;
      if (wasShown) env.close?.();
      syncTeaser();
    }

    function submit() {
      // No email address or consent claim is stored, and no request is intercepted.
      write('session', KEYS.pending, JSON.stringify({ id: config.formId, at: now() }));
    }

    return { initialize, trigger, attempt, preview, collapse, reopen, dismiss, submit, stop, get shown() { return shown; }, get collapsed() { return collapsed; } };
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { createController, createCaptchaHandoff, isVisibleChallenge, triggerDelay, scheduleTrigger, KEYS };
    return;
  }
  if (!scope.customElements || scope.customElements.get('emery-welcome-popup')) return;

  class EmeryWelcomePopup extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      const dialog = this.querySelector('dialog');
      const form = this.querySelector('form');
      if (!dialog || !form || typeof dialog.showModal !== 'function') return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      let previousFocus;
      let previousOverflow;
      const panel = dialog.querySelector('.emery-welcome__panel');
      const teaser = this.querySelector('[data-welcome-teaser]');
      const reopenButton = this.querySelector('[data-welcome-reopen]');
      const previewMode = new URLSearchParams(scope.location.search).get('welcome');
      const updateContentFit = () => {
        if (!dialog.open || !panel) return;
        const availableHeight = dialog.clientHeight;
        const contentHeight = panel.getBoundingClientRect().height;
        // Keep native scrolling unless measured content fits, with rounding tolerance.
        const fits = availableHeight > 0 && contentHeight > 0 && Number.isFinite(contentHeight)
          && contentHeight <= availableHeight + 1;
        if (dialog.hasAttribute('data-content-fits') !== fits) dialog.toggleAttribute('data-content-fits', fits);
      };
      const visible = (element) => {
        const style = scope.getComputedStyle(element);
        return !element.hidden && element.getClientRects().length > 0 && style.display !== 'none' && style.visibility !== 'hidden';
      };
      const findBlockingModal = () => Array.from(document.querySelectorAll('dialog[open], [aria-modal="true"], .drawer[open], [role="dialog"][aria-hidden="false"], #shopify-pc__banner'))
        .find((element) => !this.contains(element) && visible(element));
      const anotherModal = () => Boolean(findBlockingModal());
      const isEditing = () => {
        const active = document.activeElement;
        return active && !this.contains(active) && active.matches('input:not([type="hidden"]), textarea, select, [contenteditable="true"]');
      };

      this.controller = createController({
        config: {
          formId: this.dataset.formId,
          formState: this.querySelector('[data-welcome-result]')?.dataset.welcomeResult,
          knownCustomer: this.dataset.knownCustomer === 'true',
          pageType: this.dataset.pageType,
          repeatDays: this.dataset.repeatDays,
          preview: previewMode === 'preview',
          previewDelayed: previewMode === 'preview-delayed'
        },
        memory: pageMemory,
        storage: (kind) => kind === 'local' ? scope.localStorage : scope.sessionStorage,
        hash: () => scope.location.hash,
        pathname: () => scope.location.pathname,
        isHidden: () => document.visibilityState === 'hidden',
        isBusy: () => anotherModal() || isEditing(),
        setTeaser: (visible, expanded) => {
          if (teaser && teaser.hidden === visible) teaser.hidden = !visible;
          const value = String(expanded);
          if (reopenButton?.getAttribute('aria-expanded') !== value) reopenButton?.setAttribute('aria-expanded', value);
        },
        focusTeaser: () => reopenButton?.focus({ preventScroll: true }),
        consumeReturn: () => {
          if (scope.location.hash === `#${this.dataset.formId}`) {
            try { scope.history.replaceState(scope.history.state, '', scope.location.pathname + scope.location.search); }
            catch { /* Returning the real form result does not depend on history access. */ }
          }
        },
        open: (result) => {
          const success = this.querySelector('[data-welcome-success]');
          const signup = this.querySelector('[data-welcome-signup]');
          const error = this.querySelector('[data-welcome-error]');
          const isSuccess = result === 'success' && Boolean(success);
          signup.hidden = isSuccess;
          if (success) success.hidden = !isSuccess;
          if (error) error.hidden = result !== 'error';
          const activePanel = isSuccess ? success : signup;
          const heading = activePanel.querySelector('[data-welcome-focus]');
          const description = activePanel.querySelector('.emery-welcome__description');
          dialog.setAttribute('aria-labelledby', heading.id);
          dialog.setAttribute('aria-describedby', description.id);
          const email = signup.querySelector('input[type="email"]');
          email.setAttribute('aria-describedby', signup.querySelector('.emery-welcome__consent').id + (result === 'error' && error ? ` ${error.id}` : ''));
          if (result === 'error') email.setAttribute('aria-invalid', 'true');
          else email.removeAttribute('aria-invalid');
          if (!this.contains(document.activeElement)) previousFocus = document.activeElement;
          previousOverflow = document.documentElement.style.overflow;
          try { dialog.showModal(); } catch { return false; }
          document.documentElement.style.overflow = 'hidden';
          updateContentFit();
          (result === 'error' && error ? error : heading).focus({ preventScroll: true });
          return true;
        },
        close: () => {
          dialog.close();
          document.documentElement.style.overflow = previousOverflow || '';
          if (previousFocus?.isConnected && !this.contains(previousFocus)) previousFocus.focus({ preventScroll: true });
        }
      });

      const focusBlocker = (blocker) => {
        const target = blocker.tagName === 'IFRAME' ? blocker
          : blocker.querySelector('button:not([disabled]), a[href], input:not([disabled]):not([type="hidden"]), [tabindex]:not([tabindex="-1"])') || blocker;
        target.focus({ preventScroll: true });
      };
      this.captchaHandoff = createCaptchaHandoff({
        isActive: () => this.isConnected && this.controller.shown,
        findChallenge: () => Array.from(document.querySelectorAll('iframe[title]')).find((frame) => isVisibleChallenge(frame, dialog, scope)),
        findBlockingModal,
        focusBlocker,
        suspend: (blocker) => {
          if (!dialog.open) return false;
          // Cookie consent and other modals can arrive after this dialog opens.
          // Yield the inert top layer without dismissing or replacing our form.
          dialog.close();
          document.documentElement.style.overflow = previousOverflow || '';
          focusBlocker(blocker);
          return true;
        },
        canResume: () => document.visibilityState !== 'hidden' && !anotherModal(),
        resume: () => {
          try { dialog.showModal(); } catch { return false; }
          document.documentElement.style.overflow = 'hidden';
          updateContentFit();
          form.querySelector('input[type="email"]')?.focus({ preventScroll: true });
          return true;
        }
      });

      this.querySelectorAll('[data-welcome-close]').forEach((button) => button.addEventListener('click', () => this.controller.collapse(), { signal }));
      reopenButton?.addEventListener('click', () => this.controller.reopen(), { signal });
      this.querySelector('[data-welcome-dismiss]')?.addEventListener('click', () => {
        this.controller.dismiss();
        if (previousFocus?.isConnected && !this.contains(previousFocus)) previousFocus.focus({ preventScroll: true });
      }, { signal });
      // The modal X collapses this offer; Escape and the backdrop do not close it.
      dialog.addEventListener('cancel', (event) => { event.preventDefault(); }, { signal });
      // Native dialog provides focus containment; native form validation
      // runs before this event. Shopify remains in charge of submission and captcha.
      form.addEventListener('submit', () => {
        this.controller.submit();
        this.captchaHandoff.arm();
      }, { signal });
      const retry = () => { this.controller.attempt(); this.captchaHandoff.check(); updateContentFit(); };
      document.addEventListener('visibilitychange', retry, { signal });
      document.addEventListener('dialog:after-hide', retry, { signal });
      document.addEventListener('focusout', () => {
        scope.clearTimeout(this.focusRetry);
        this.focusRetry = scope.setTimeout(retry, 200);
      }, { signal });
      this.observer = new MutationObserver(retry);
      this.observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['open', 'hidden', 'aria-hidden', 'aria-modal', 'style', 'class', 'title'] });
      if (scope.ResizeObserver && panel) {
        this.contentResizeObserver = new scope.ResizeObserver(updateContentFit);
        this.contentResizeObserver.observe(dialog);
        this.contentResizeObserver.observe(panel);
      }
      scope.addEventListener('resize', () => { this.captchaHandoff.check(); updateContentFit(); }, { passive: true, signal });
      scope.addEventListener('scroll', () => this.captchaHandoff.check(), { passive: true, signal });
      document.addEventListener('shopify:section:select', (event) => {
        if (this.dataset.designMode === 'true' && event.target.contains(this)) this.controller.preview();
      }, { signal });

      const { needsTrigger } = this.controller.initialize();
      if (needsTrigger) {
        // Automatic opening is timer-only; scrolling and exit intent cannot shorten it.
        this.timer = scheduleTrigger({
          delay: previewMode === 'preview-delayed' ? 5 : this.dataset.delay,
          trigger: () => this.controller.trigger(),
          setTimeout: (callback, delay) => scope.setTimeout(callback, delay)
        });
      }
    }

    disconnectedCallback() {
      scope.clearTimeout(this.timer);
      scope.clearTimeout(this.focusRetry);
      this.abort?.abort();
      this.observer?.disconnect();
      this.contentResizeObserver?.disconnect();
      this.captchaHandoff?.stop();
      this.controller?.stop();
      this.controller = null;
    }
  }
  scope.customElements.define('emery-welcome-popup', EmeryWelcomePopup);
})(typeof window === 'undefined' ? globalThis : window);
